// App.js
import React, { useEffect, useRef } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import SnackbarProvider from "@codejq/react-simple-snackbar";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { getCompany } from "./actions/companyActions";
import { WEBSOCKET_CONNECT, WEBSOCKET_DISCONNECT } from "./actions/constants";
import lngs from "./lngs";
import TopNav from "./components/TopNav/TopNav";
import Footer from "./components/Footer/Footer";
import Header from "./components/Header/Header";
import RTL from "./utils/stylesprovider";
import DateProvider from "./components/Shared/DateProvider";
import Seo from "./components/Seo/Seo";

// Import the routing component we created
import Routes from "./Routes";
import AppSubscriptionWrapper from "./components/Subscription/AppSubscriptionWrapper";

function App() {
  const { i18n } = useTranslation();
  const user = JSON.parse(localStorage.getItem("profile"));
  const currentLang = i18n.resolvedLanguage;
  const lang = window.location.pathname.split("/")[1] || "en";
  const isInApp = window.location.pathname.includes("/app/");
  const isRemovedAppLanding = /\/app\/(main|accounting)\/?$/i.test(
    window.location.pathname,
  );
  const isModernPOSTerminal = window.location.pathname.includes("/app/pos/terminal/");
  const dispatch = useDispatch();
  const socketInitialized = useRef(false);
  const userIdRef = useRef(null);

  // Handle language changes
  useEffect(() => {
    if (currentLang !== lang && lngs[lang]?.nativeName?.length > 0) {
      i18n.changeLanguage(lang);
    }
  }, [currentLang, lang, i18n]);

  // Fetch company data
  useEffect(() => {
    if (user?.result?.companyId) {
      dispatch(getCompany(user.result.companyId));
    }
  }, [dispatch, user?.result?.companyId]);

  // Handle WebSocket connection
  useEffect(() => {
    const userId = user?.result?._id;

    if (
      userId &&
      (!socketInitialized.current || userId !== userIdRef.current)
    ) {
      console.log("Initializing WebSocket for user:", userId);
      socketInitialized.current = true;
      userIdRef.current = userId;

      dispatch({
        type: WEBSOCKET_CONNECT,
        payload: {
          token: user?.token,
          userId,
        },
      });

      return () => {
        console.log("Disconnecting WebSocket for user:", userIdRef.current);
        socketInitialized.current = false;
        dispatch({ type: WEBSOCKET_DISCONNECT });
      };
    }

    // If user logs out, disconnect
    if (!userId && socketInitialized.current) {
      console.log("User logged out, disconnecting WebSocket");
      socketInitialized.current = false;
      userIdRef.current = null;
      dispatch({ type: WEBSOCKET_DISCONNECT });
    }
  }, [dispatch, user?.result?._id, user?.token]);

  // RTL or LTR setup
  const isLanguageRTL = lngs[i18n.resolvedLanguage]?.isRtl || false;
  useEffect(() => {
    document.documentElement.setAttribute("dir", isLanguageRTL ? "rtl" : "ltr");
  }, [isLanguageRTL]);

  return (
    <DateProvider>
      <RTL isRTL={isLanguageRTL}>
        <div dir={isLanguageRTL ? "rtl" : "ltr"}>
          <Router>
            <SnackbarProvider>
              <AppSubscriptionWrapper>
                <Seo />
                {/* Fixed top header bar */}
                <Header />

                {/* Fixed teal navigation bar (app-only) */}
                {user && <TopNav />}

                {/* Spacer to push content below fixed Header + TopNav */}
                {isInApp && user && !isRemovedAppLanding && !isModernPOSTerminal && (
                  <div className="app-top-spacer" />
                )}

                {/* All your routes go here */}
                <Routes />

                <Footer />

              </AppSubscriptionWrapper>
            </SnackbarProvider>
          </Router>
        </div>
      </RTL>
    </DateProvider>
  );
}

export default App;
